import React, {useEffect} from 'react';
import {RouterProvider} from 'react-router/dom';
import {router} from './router';
import {useAuthExpirationGuard} from '@utils/hooks/useAuthExpirationGuard';
import {ThemeProvider} from '@components/theme/ThemeProvider';
import i18next from 'i18next';
import {Storage} from '@utils/storage';
import {LANGUAGE_STORAGE_KEY, normalizeLanguageCode} from '@utils/language';
import {useLazyFetchTranslationResourcesQuery} from '@goproblems/shared/api/tools';
import {ApplicationTranslationResources} from '@types';
import {useTranslation} from 'react-i18next';
import {UiProvider} from '@goproblems/ui';

const getPreferredLanguage = () => {
    try {
        const auth = Storage.getObjData('auth');
        const authLanguage = auth && auth.user ? auth.user.language : null;

        return authLanguage || Storage.getData(LANGUAGE_STORAGE_KEY) || 'en';
    } catch {
        return Storage.getData(LANGUAGE_STORAGE_KEY) || 'en';
    }
};

const getTranslationsForLanguage = (
    resources: ApplicationTranslationResources | null,
    language: string
) => {
    const resourceKey = Object.keys(resources || {}).find(
        lang => normalizeLanguageCode(lang) === language
    );

    return resourceKey ? resources?.[resourceKey]?.translation : null;
};

const Translation = () => {
    const [fetchTranslationResources] = useLazyFetchTranslationResourcesQuery();

    useEffect(() => {
        let isMounted = true;

        async function loadUserLanguage() {
            const language = normalizeLanguageCode(getPreferredLanguage());
            Storage.storeData(LANGUAGE_STORAGE_KEY, language);

            const loadedResources =
                language !== 'en'
                ? await fetchTranslationResources(language).unwrap()
                : null;
            if (!isMounted) return;

            const translations = getTranslationsForLanguage(
                loadedResources,
                language
            );
            if (translations) {
                i18next.addResourceBundle(
                    language,
                    'translation',
                    translations,
                    true,
                    true
                );
            }

            await i18next.changeLanguage(language);
        }

        void loadUserLanguage().catch(error => {
            console.warn('Failed to load user language', error);
        });

        return () => {
            isMounted = false;
        };
    }, [fetchTranslationResources]);

    return null;
};

const App = () => {
    const {t} = useTranslation();
    useAuthExpirationGuard();

    useEffect(() => {
        if ('serviceWorker' in navigator) {
            void navigator.serviceWorker.ready
                .then(registration => {
                    if (registration.active) {
                        registration.active.postMessage({
                            type: 'LOAD_NOTIFICATIONS',
                        });
                    }
                })
                .catch(error => {
                    console.warn('Service Worker not available:', error);
                });
        }
    }, []);

    useEffect(() => {
        const intervalId = setInterval(() => {
            if ('serviceWorker' in navigator) {
                void navigator.serviceWorker.ready
                    .then(registration => {
                        if (registration.active) {
                            registration.active.postMessage({
                                type: 'LOAD_NOTIFICATIONS',
                            });
                        }
                    })
                    .catch(error => {
                        console.warn('Service Worker not available:', error);
                    });
            }
        }, 20 * 1000);

        return () => clearInterval(intervalId);
    }, []);

    return (
        <React.StrictMode>
            <UiProvider
                labels={{
                    close: t('common.close'),
                    loading: t('common.loading'),
                    toggleNavigation: t('common.toggle_navigation'),
                }}>
                <ThemeProvider>
                    <RouterProvider router={router} />
                    <Translation />
                </ThemeProvider>
            </UiProvider>
        </React.StrictMode>
    );
};

export default App;
